import { useEffect, useState } from "react";
import { 
	Flex,
	SimpleGrid,
	Text,
	Switch,
	Select,
	FormControl,
	Input,
	Icon,
	Badge
} from "@chakra-ui/react";
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { search } from 'ss-search';

import api from "../../services/api";
import { useAuth } from "../../hooks/auth";
import { useCart } from "../../hooks/cart";
import Header from "../../components/Header";
import Loading from "../../components/Loading";
import Cart from "../../components/Cart";
import Pedido from "../../components/Pedidos/pedido";
import { RiSearchLine } from "react-icons/ri";
import PedidoDrawer from "../../components/Pedidos/pedidoDrawer";
import useDebounce from "../../utils/useDebounce";

const perPage = [
	{label: 'tudo', value: 0},
	{ label: '1 semana', value: 7 },
	{ label: '15 dias', value: 15 },
	{ label: '1 mês', value: 30 },
	{ label: '2 meses', value: 60 },
	{ label: '3 mese', value: 90 },
	{ label: '6 meses', value: 180 },
	{ label: '1 ano', value: 365 }
];

type SearchFormData = {
	search: string;
}
type PedidoData = {
	cliente: string;
	date_ano: number;
	date_dia: number;
	date_mes: string;
	date_venda: string;
	des_status: string;
	emp_cod: number;
	id_order: number;
	item_nome: string;
	json_order: string;
	loja: string;
	operacao: string;
	prev_entrega: string;
	prev_tipo: string;
	qtd: number;
	total: number;
	view_status: string;
}

const searchFormSchema = yup.object().shape({
	search: yup.string(),
});

const Pedidos: React.FC = () => {
	const { user, company } = useAuth();
	const { cart } = useCart();

	const [loading, setLoading] = useState(true);
	const [loadingOrders, setLoadingOrders] = useState(false);
	const [switchCliente, setSwitchCliente] = useState(cart.cliente.id ? true : false);
	const [switchVendedor, setSwitchVendedor] = useState(false);

	const [pedidos, setPedidos] = useState<PedidoData[]>([]);
	const [searchPedidos, setSearchPedidos] = useState<PedidoData[]>([]);
	const [periodo, setPeriodo] = useState(0);

	const [isOpenCart, setIsOpenCart] = useState(false);

	const [isOpenPedidoDrawer, setIsOpenPedidoDrawer] = useState(false);
	const [pedidoDrawer, setPedidoDrawer] = useState({} as any);

	const debounceChange = useDebounce(handleSearch, 700);

	const { register, reset } = useForm<SearchFormData>({
		resolver: yupResolver(searchFormSchema)
	})

	useEffect(() => {
		setLoadingOrders(true);
		const cliente = cart.cliente.id && switchCliente ? cart.cliente.id : -1;
		const vendedor = switchVendedor ? user.id : 0;

		api.get(`controle/pedido/${cliente}/${periodo}/${vendedor}`).then(response => {
			setPedidos(response.data);
			setSearchPedidos(response.data);

			reset();

			setLoading(false);
			setLoadingOrders(false);
		}).catch(() => {
			setLoading(false);
			setLoadingOrders(false);
		});
	}, [cart.cliente.id, user.id, reset, switchCliente, periodo, switchVendedor, company]);

	function handleSearch(event: string) {
		const texto = event;

		if (texto === '' || texto === ' ') {
			setSearchPedidos(pedidos);
			return;
		}

		const searchKeys = ['cliente', 'loja', 'operacao', 'json_order'];
		const result = search(pedidos, searchKeys, texto) as PedidoData[];

		setSearchPedidos(result);
	};

	return (
		<Flex
			w="100vw"
			h="100vh"
			flexDir="column"
			align="center"
			overflowX="hidden"
		>
			<Header isOpenCart={setIsOpenCart} />

			{loading ? (
				<Loading />
			) : (
				<Flex
					flexDir="column"
					align="center"
					w="100vw"
					h="100vh"
					pb="6"
				>
					{/* PARAMETROS DE BUSCA */}
					<Flex
						w={["99%", "80%"]}
						mt={["16", "20", "20", "20"]}
						px="2"
						flexDir={["column", "column", "column", "row"]}
						pb="4"
						align={["flex-start", "flex-start", "flex-start", "center"]}
					>
						<Flex fontWeight="500" align="center" mb={["2", "2", "2", "0"]} pr="4">
							<Flex flexDir="column" pr="4">
								{cart.consumidor && <Badge fontSize="9" borderRadius={8}>Consumidor</Badge>}

								<Text>
									{cart.cliente.id
										? cart.cliente.nome
										: 'Nenhum cliente selecionado'
									}
								</Text>
								{cart.cliente.doc && (
									<Text fontSize="13" fontWeight="500" color="gray.400">{cart.cliente.doc}</Text>
								)}
							</Flex>

							<Switch
								colorScheme="blue"
								isChecked={switchCliente}
								onChange={() => setSwitchCliente(!switchCliente)}
								isDisabled={!cart.cliente.id}
							/>
						</Flex>

							<Flex
								w="auto"
								align="center"
								px={["0", "0", "0", "4"]}
							>
								<FormControl w="125px" mr="4">
									<Select
										name="periodo"
										size="sm"
										variant="filled"
										fontWeight="500"
										w="120px"
										borderRadius={8}
										onChange={e => {
											setPeriodo(Number(e.target.value));
										}}
									>
										{perPage.map(pPage => {
											return (
												<option key={pPage.value} value={pPage.value}>{pPage.label}</option>
											)
										})}
									</Select>
								</FormControl>

								<Flex align="end" flexDir="row">
									<Text fontWeight="500" pr="4">Somente minhas vendas</Text>
									<Switch
										colorScheme="blue"
										isChecked={switchVendedor}
										onChange={() => setSwitchVendedor(!switchVendedor)}
									/>
							</Flex>
						</Flex>
					</Flex>

					{/* CAMPO DE PESQUISA */}
					<Flex pb="4" w={["99%", "80%"]} px="4">
						<Flex
							as="form"
							py={["1", "2"]}
							pl="2"
							pr="4"
							mr="4"
							alignSelf="center"
							color="gray.900"
							position="relative"
							bg="gray.50"
							borderRadius="full"
						>
							<Input
								fontSize={["14", "16"]}
								color="gray.900"
								variant="unstyled"
								px="4"
								mr="4"
								placeholder="Pesquisar"
								_placeholder={{ color: 'gray.400' }}
								{...register("search")}
								onChange={e => debounceChange(e.target.value)}
								//onChange={e => handleSearch(e.target.value)}
							/>

							<Icon as={RiSearchLine} fontSize={["18", "20"]} color="gray.400" />
						</Flex>
					</Flex>

					{loadingOrders ? (
						<Loading />
					) : (
						<Flex
							flexDir="column"
							justify="center"
							w={["99%", "80%"]}
						>
							<SimpleGrid columns={[1, 1, 1, 2]} spacingX='6' spacingY='6'>
								{searchPedidos.map((pedido, indice) => {
									return (
										<Pedido
											key={indice}
											pedido={pedido}
											setPedidoDrawer={setPedidoDrawer}
											setIsOpenPedidoDrawer={setIsOpenPedidoDrawer}
										/>
									)
								})}
							</SimpleGrid>
						</Flex>
					)}
				</Flex>
			)}

			<Cart
				isOpen={isOpenCart}
				onRequestClose={setIsOpenCart}
			/>

			{/* isOpenPedidoDrawer && ( */}
				<PedidoDrawer
					isOpen={isOpenPedidoDrawer}
					onRequestClose={setIsOpenPedidoDrawer}
					pedido={pedidoDrawer}
				/>
			{/* }) */}
		</Flex>
	)
};

export default Pedidos;
